import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertDescription, Button, Spinner } from '@klaro7/ui';

import { useAdminUserById } from '@/features/users/hooks/useAdminUsers';
import { useUserActivity } from '@/features/user-activity/hooks/useUserActivity';
import { ActivityEntryRow } from '@/features/user-activity/components/ActivityEntryRow';
import { LEVEL_FILTERS, TYPE_FILTERS } from '@/features/user-activity/lib/activityMeta';

/**
 * Storico attività di un utente (super-admin): timeline completa, dal più
 * recente, con filtri per tipo e livello e "carica altri".
 */
export function AdminUserTimelinePage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [type, setType] = useState('');
  const [level, setLevel] = useState('');

  const { data: user } = useAdminUserById(id);
  const { data, isLoading, isError, isFetching, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useUserActivity(id, { type: type || undefined, level: level || undefined });

  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const total = data?.pages[0]?.pagination.total ?? 0;
  const fullName = user ? `${user.profile?.firstName ?? ''} ${user.profile?.lastName ?? ''}`.trim() || user.email : '';

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(`/dashboard/admin/users/${id}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={goBack}
            aria-label="Indietro"
            className="mt-0.5 rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Storico attività</h1>
            <p className="mt-1 text-sm text-neutral-500">
              {fullName ? (
                <>
                  <Link to={`/dashboard/admin/users/${id}`} className="font-medium text-brand-teal hover:underline">
                    {fullName}
                  </Link>
                  {user?.email && fullName !== user.email ? ` · ${user.email}` : ''}
                </>
              ) : (
                'Tutto ciò che è successo su questo account, dal più recente.'
              )}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:opacity-50"
        >
          {isFetching && !isFetchingNextPage ? 'Aggiorno…' : 'Aggiorna'}
        </button>
      </div>

      {/* Filtri */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {LEVEL_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setLevel(f.value)}
              className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                level === f.value
                  ? f.value === 'error'
                    ? 'bg-red-600 text-white'
                    : 'bg-brand-teal text-white'
                  : 'border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-neutral-600">
          <span className="shrink-0">Tipo</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm text-neutral-800 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal sm:w-56"
          >
            {TYPE_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isError && (
        <Alert variant="error">
          <AlertDescription>Errore nel caricamento dello storico. Riprova più tardi.</AlertDescription>
        </Alert>
      )}

      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-sm text-neutral-500">
            {type || level ? 'Nessun evento con questi filtri.' : 'Nessuna attività registrata per questo utente.'}
          </div>
        ) : (
          <>
            <div className="border-b border-neutral-100 px-4 py-2 text-xs text-neutral-500">
              {items.length} di {total} eventi
            </div>
            <ul className="divide-y divide-neutral-100">
              {items.map((e) => (
                <ActivityEntryRow key={e._id} entry={e} />
              ))}
            </ul>
          </>
        )}
      </div>

      {hasNextPage && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? 'Carico…' : 'Carica altri'}
          </Button>
        </div>
      )}

      <p className="text-xs text-neutral-400">
        Lo storico registra gli eventi a partire dall'attivazione di questa funzione: le attività precedenti non sono
        presenti. Codici OTP, password e numeri completi non vengono mai salvati.
      </p>
    </div>
  );
}
